import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Radio,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { createActivityRule, updateActivityRule } from './api';
import type {
  ActivityRule,
  RewardSetting,
  RewardDistributionItem,
  SubmissionRequirement,
  AwardCriterion,
  CommonQuestion,
  ImportantNote,
} from './types';
import { contestTypeMap } from '@/pages/vote-activity/vote/type';
import styles from './ActivityRuleDrawer.module.less';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

interface ActivityRuleDrawerProps {
  visible: boolean;
  editData?: ActivityRule;
  onSuccess: () => void;
  onClose: () => void;
}

// 将 contestTypeMap 转换为下拉框选项格式，value 使用 key（枚举值）
const ACTIVITY_TYPE_OPTIONS = Array.from(contestTypeMap.entries()).map(
  ([key, label]) => ({
    label,
    value: key,
  })
);

const ActivityRuleDrawer: React.FC<ActivityRuleDrawerProps> = ({
  visible,
  editData,
  onSuccess,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // 使用ref存储各个Form.List的add函数
  const rewardSettingsAddRef = useRef<(() => void) | null>(null);
  const rewardDistributionAddRef = useRef<(() => void) | null>(null);
  const submissionRequirementsAddRef = useRef<(() => void) | null>(null);
  const awardCriteriaAddRef = useRef<(() => void) | null>(null);
  const commonQuestionsAddRef = useRef<(() => void) | null>(null);
  const importantNotesAddRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (visible) {
      const initial = editData
        ? {
            ...editData,
            dateRange:
              editData.startTime && editData.endTime
                ? [
                    dayjs(Number(editData.startTime)),
                    dayjs(Number(editData.endTime)),
                  ]
                : undefined,
            rewardSettings: editData.rewardSettings || [
              { descriptionEn: '', descriptionJa: '' },
            ],
            rewardDistribution: editData.rewardDistribution || [
              { descriptionEn: '', descriptionJa: '' },
            ],
            submissionRequirements: editData.submissionRequirements || [
              { requirementEn: '', requirementJa: '' },
            ],
            awardCriteria: editData.awardCriteria || [
              { criterionEn: '', criterionJa: '' },
            ],
            commonQuestions: editData.commonQuestions || [
              { titleEn: '', titleJa: '', contentEn: '', contentJa: '' },
            ],
            importantNotes: editData.importantNotes || [
              { noteEn: '', noteJa: '' },
            ],
            status: editData.status ?? 1,
          }
        : {
            rewardSettings: [{ descriptionEn: '', descriptionJa: '' }],
            rewardDistribution: [{ descriptionEn: '', descriptionJa: '' }],
            submissionRequirements: [{ requirementEn: '', requirementJa: '' }],
            awardCriteria: [{ criterionEn: '', criterionJa: '' }],
            commonQuestions: [
              { titleEn: '', titleJa: '', contentEn: '', contentJa: '' },
            ],
            importantNotes: [{ noteEn: '', noteJa: '' }],
            status: 1,
          };
      form.setFieldsValue(initial);
    } else {
      form.resetFields();
    }
  }, [visible, editData, form]);

  const onSubmit = async (values: any) => {
    const [startTime, endTime] = values.dateRange || [];

    const params: ActivityRule = {
      ...values,
      startTime: startTime?.valueOf(),
      endTime: endTime?.valueOf(),
      id: editData?.id,
    };
    delete params.dateRange;

    // 打印所有表单参数
    console.log('=== 表单参数 ===');
    console.log('原始表单值:', values);
    console.log('处理后的参数:', params);
    console.log('================');

    const resp = editData
      ? await updateActivityRule(params)
      : await createActivityRule(params);

    if (resp.status === 200) {
      messageApi.success(`${editData ? '更新' : '创建'}成功`);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 500);
    } else {
      messageApi.error(resp.message || `${editData ? '更新' : '创建'}失败`);
    }
  };

  return (
    <Drawer
      open={visible}
      width={800}
      onClose={onClose}
      destroyOnClose
      maskClosable={false}
      className={styles.activityRuleDrawer}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{editData ? '编辑' : '新建'}活动规则</span>
          <div>
            <Button onClick={onClose} style={{ marginRight: 8 }}>
              取消
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                try {
                  await form.validateFields();
                  form.submit();
                } catch (error) {
                  messageApi.error('请填写所有必填项');
                }
              }}
            >
              保存
            </Button>
          </div>
        </div>
      }
    >
      {contextHolder}
      <Form
        layout="vertical"
        form={form}
        onFinish={onSubmit}
        style={{ width: 600 }}
      >
        <Form.Item
          name="activityType"
          label="活动类型"
          rules={[{ required: true, message: '请选择活动类型' }]}
        >
          <Select
            placeholder="选择要创建的活动类型"
            options={ACTIVITY_TYPE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name="activityTopicEn"
          label={
            <span>
              {/* <span style={{ marginRight: 8 }}>500</span> */}
              活动主题(英语)
            </span>
          }
          rules={[{ required: true, message: '请输入活动主题(英语)' }]}
        >
          <Input
            placeholder="请输入活动主题(英语)"
            maxLength={500}
            showCount={{
              formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
            }}
          />
        </Form.Item>

        <Form.Item
          name="activityTopicJa"
          label={
            <span>
              {/* <span style={{ marginRight: 8 }}>160</span> */}
              活动主题(日语)
            </span>
          }
          rules={[{ required: true, message: '请输入活动主题(日语)' }]}
        >
          <Input
            placeholder="请输入活动主题(日语)"
            maxLength={160}
            showCount={{
              formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
            }}
          />
        </Form.Item>

        <Form.Item
          name="descriptionEn"
          label="描述(英语)"
          rules={[{ required: true, message: '请输入活动简介描述(英语)' }]}
        >
          <TextArea placeholder="请输入活动简介描述(英语)" rows={4} />
        </Form.Item>

        <Form.Item
          name="descriptionJa"
          label="描述(日语)"
          rules={[{ required: true, message: '请输入活动简介描述(日语)' }]}
        >
          <TextArea placeholder="请输入活动简介描述(日语)" rows={4} />
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="日期"
          rules={[{ required: true, message: '请选择日期范围' }]}
        >
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder={['开始时间', '结束时间']}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label={
            <div
              key="reward-settings-label"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <span style={{fontSize: 18, fontWeight: 700}}>奖品设置</span>
              <div
                onClick={() => rewardSettingsAddRef.current?.()}
                className={styles.addButton}
              >
                <PlusOutlined />
                新增一栏
              </div>
            </div>
          }
          required
          style={{ marginTop: 30 }}
        >
          <Form.List name="rewardSettings">
            {(fields, { add, remove }) => {
              rewardSettingsAddRef.current = add;
              return (
                <>
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    return (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            marginBottom: 8,
                            display: 'flex',
                            justifyContent: 'space-between',
                            color: "#B1B5C3"
                          }}
                        >
                          <div>设置{index + 1}</div>
                          <div>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                onClick={() => remove(field.name)}
                                style={{
                                  color: '#ff4d4f',
                                  cursor: 'pointer',
                                  fontSize: 16,
                                }}
                              />
                            )}
                          </div>
                        </div>
                        {/* <Space align="baseline" style={{ width: '100%' }}> */}
                        <Form.Item
                          {...restField}
                          name={[field.name, 'descriptionEn']}
                          rules={[
                            { required: true, message: '请输入奖品描述(英语)' },
                          ]}
                          style={{ marginBottom: 10 }}
                        >
                          <Input placeholder="请输入奖品描述(英语)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[field.name, 'descriptionJa']}
                          rules={[
                            { required: true, message: '请输入奖品描述(日语)' },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="请输入奖品描述(日语)" />
                        </Form.Item>
                        {/* </Space> */}
                      </div>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Form.Item>

        <Form.Item
          label={
            <div
              key="reward-distribution-label"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <span style={{fontSize: 18, fontWeight: 700}}>奖励发放说明</span>
              <div
                onClick={() => rewardDistributionAddRef.current?.()}
                className={styles.addButton}
              >
                <PlusOutlined />
                新增一栏
              </div>
            </div>
          }
          required
          style={{ marginTop: 30 }}
        >
          <Form.List name="rewardDistribution">
            {(fields, { add, remove }) => {
              rewardDistributionAddRef.current = add;
              return (
                <>
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    return (
                      <div key={key} style={{ marginBottom: 16, color: "#B1B5C3" }}>
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                          <div>奖励发放说明{index + 1}</div>
                          <div>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                onClick={() => remove(field.name)}
                                style={{
                                  color: '#ff4d4f',
                                  cursor: 'pointer',
                                  fontSize: 16,
                                }}
                              />
                            )}
                          </div>
                        </div>
                        {/* <Space align="baseline" style={{ width: '100%' }}> */}
                        <Form.Item
                          {...restField}
                          name={[field.name, 'descriptionEn']}
                          rules={[
                            {
                              required: true,
                              message: '请输入奖励发放说明(英语)',
                            },
                          ]}
                          style={{ marginBottom: 10 }}
                        >
                          <Input placeholder="请输入奖励发放说明(英语)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[field.name, 'descriptionJa']}
                          rules={[
                            {
                              required: true,
                              message: '请输入奖励发放说明(日语)',
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="请输入奖励发放说明(日语)" />
                        </Form.Item>
                        {/* </Space> */}
                      </div>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Form.Item>

        <Form.Item
          label={
            <div
              key="submission-requirements-label"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <span style={{fontSize: 18, fontWeight: 700}}>参赛作品要求</span>
              <div
                onClick={() => submissionRequirementsAddRef.current?.()}
                className={styles.addButton}
              >
                <PlusOutlined />
                新增一栏
              </div>
            </div>
          }
          required
          style={{ marginTop: 30 }}
        >
          <Form.List name="submissionRequirements">
            {(fields, { add, remove }) => {
              submissionRequirementsAddRef.current = add;
              return (
                <>
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    return (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            marginBottom: 8,
                            color: "#B1B5C3",
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>要求{index + 1}</div>
                          <div>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                onClick={() => remove(field.name)}
                                style={{
                                  color: '#ff4d4f',
                                  cursor: 'pointer',
                                  fontSize: 16,
                                }}
                              />
                            )}
                          </div>
                        </div>
                        {/* <Space align="baseline" style={{ width: '100%' }}> */}
                        <Form.Item
                          {...restField}
                          name={[field.name, 'requirementEn']}
                          rules={[
                            {
                              required: true,
                              message: '请输入参赛作品要求(英语)',
                            },
                          ]}
                          style={{ marginBottom: 10 }}
                        >
                          <Input placeholder="请输入参赛作品要求(英语)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[field.name, 'requirementJa']}
                          rules={[
                            {
                              required: true,
                              message: '请输入参赛作品要求(日语)',
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="请输入参赛作品要求(日语)" />
                        </Form.Item>

                        {/* </Space> */}
                      </div>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Form.Item>

        <Form.Item
          label={
            <div
              key="award-criteria-label"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <span style={{fontSize: 18, fontWeight: 700}}>获奖作品评判标准</span>
              <div
                onClick={() => awardCriteriaAddRef.current?.()}
                className={styles.addButton}
              >
                <PlusOutlined />
                新增一栏
              </div>
            </div>
          }
          required
          style={{ marginTop: 30 }}
        >
          <Form.List name="awardCriteria">
            {(fields, { add, remove }) => {
              awardCriteriaAddRef.current = add;
              return (
                <>
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    return (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            marginBottom: 8,
                            color: "#B1B5C3",
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>标准{index + 1}</div>
                          <div>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                onClick={() => remove(field.name)}
                                style={{
                                  color: '#ff4d4f',
                                  cursor: 'pointer',
                                  fontSize: 16,
                                }}
                              />
                            )}
                          </div>
                        </div>
                        {/* <Space align="baseline" style={{ width: '100%' }}> */}
                        <Form.Item
                          {...restField}
                          name={[field.name, 'criterionEn']}
                          rules={[
                            {
                              required: true,
                              message: '请输入作品评判标准(英语)',
                            },
                          ]}
                          style={{ marginBottom: 10 }}
                        >
                          <Input placeholder="请输入作品评判标准(英语)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[field.name, 'criterionJa']}
                          rules={[
                            {
                              required: true,
                              message: '请输入作品评判标准(日语)',
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="请输入作品评判标准(日语)" />
                        </Form.Item>

                        {/* </Space> */}
                      </div>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Form.Item>

        <Form.Item
          label={
            <div
              key="common-questions-label"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <span style={{fontSize: 18, fontWeight: 700}}>常见问题</span>
              <div
                onClick={() => commonQuestionsAddRef.current?.()}
                className={styles.addButton}
              >
                <PlusOutlined />
                新增一栏
              </div>
            </div> 
          }
          style={{ marginTop: 30 }}
        >
          <Form.List name="commonQuestions">
            {(fields, { add, remove }) => {
              commonQuestionsAddRef.current = add;
              return (
                <>
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    return (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            marginBottom: 8,
                            color: "#B1B5C3",
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>问题{index + 1}</div>
                          <div>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                onClick={() => remove(field.name)}
                                style={{
                                  color: '#ff4d4f',
                                  cursor: 'pointer',
                                  fontSize: 16,
                                }}
                              />
                            )}
                          </div>
                        </div>
                        <Space
                          direction="vertical"
                          style={{ width: '100%' }}
                          size="middle"
                        >
                          {/* <Space align="baseline" style={{ width: '100%' }}> */}
                          <Form.Item
                            {...restField}
                            name={[field.name, 'titleEn']}
                            label="标题(英语)"
                            style={{ marginBottom: 10 }}
                          >
                            <Input placeholder="请输入问题标题(英语)" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[field.name, 'titleJa']}
                            label="标题(日语)"
                            style={{ marginBottom: 10 }}
                          >
                            <Input placeholder="请输入问题标题(日语)" />
                          </Form.Item>
                          {/* </Space> */}
                          {/* <Space align="baseline" style={{ width: '100%' }}> */}
                          <Form.Item
                            {...restField}
                            name={[field.name, 'contentEn']}
                            label="内容(英语)"
                            style={{ marginBottom: 10 }}
                          >
                            <TextArea
                              placeholder="请输入问题解答(英语)"
                              rows={3}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[field.name, 'contentJa']}
                            label="内容(日语)"
                            style={{ marginBottom: 0 }}
                          >
                            <TextArea
                              placeholder="请输入问题解答(日语)"
                              rows={3}
                            />
                          </Form.Item>
                          {/* </Space> */}
                        </Space>
                      </div>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Form.Item>

        <Form.Item
          label={
            <div
              key="important-notes-label"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <span style={{fontSize: 18, fontWeight: 700}}>重要提示</span>
              <div
                onClick={() => importantNotesAddRef.current?.()}
                className={styles.addButton}
              >
                <PlusOutlined />
                新增一栏
              </div>
            </div>
          }
          style={{ marginTop: 30 }}
        >
          <Form.List name="importantNotes">
            {(fields, { add, remove }) => {
              importantNotesAddRef.current = add;
              return (
                <>
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    return (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            marginBottom: 8,
                            color: "#B1B5C3",
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>提示{index + 1}</div>
                          <div>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                onClick={() => remove(field.name)}
                                style={{
                                  color: '#ff4d4f',
                                  cursor: 'pointer',
                                  fontSize: 16,
                                }}
                              />
                            )}
                          </div>
                        </div>
                        {/* <Space align="baseline" style={{ width: '100%' }}> */}
                        <Form.Item
                          {...restField}
                          name={[field.name, 'noteEn']}
                          // rules={[
                          //   { required: true, message: '请输入提示内容(英语)' },
                          // ]}
                          style={{ marginBottom: 10 }}
                        >
                          <Input placeholder="请输入提示内容(英语)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[field.name, 'noteJa']}
                          // rules={[
                          //   { required: true, message: '请输入提示内容(日语)' },
                          // ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="请输入提示内容(日语)" />
                        </Form.Item>
                        {/* </Space> */}
                      </div>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Form.Item>

        <Form.Item
          name="status"
          label="启用状态"
          rules={[{ required: true, message: '请选择启用状态' }]}
        >
          <Radio.Group>
            <Radio value={1}>启用</Radio>
            <Radio value={0}>禁用</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ActivityRuleDrawer;
